import React, { useState, useMemo, useRef } from "react";
import {
  Trash2,
  Calendar,
  Loader2,
  Upload,
  SquarePen,
  Plus,
  Repeat,
} from "lucide-react";
import toast from "react-hot-toast";
import { ConfirmationModal } from "../common/ConfirmationModal";
import HolidayBulkUploadModal from "./HolidayBulkUploadModal";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import {
  Button,
  PageHeader,
  Select,
  Modal,
  ModalFooter,
} from "../common";
import { HolidaysSectionSkeleton } from "./Skeleton";
import dayjs from "dayjs";
import { getHolidayYears } from "../../utils/holidayUtils";
import Input from "../common/Input";
import RadioButton from "../common/RadioButton";
import { TextArea } from "../common/TextArea";
import {
  useGetHolidaysQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
  IHoliday,
} from "../../store/apis/holidayManagement.api";

interface IApiError {
  data?: {
    message?: string;
  };
  message?: string;
}
interface IHolidayFormData {
  name: string;
  date: string;
  year: string;
  isMandatory: boolean;
  description?: string;
  isRecurring: boolean;
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEKDAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

const HolidayManagement: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const canManage = useHasPermission(PERMISSIONS.HOLIDAY_MANAGEMENT_MANAGE);
  const formRef = useRef<HTMLDivElement>(null);

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<IHoliday | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState<IHoliday | null>(null);
  const [deleteSeriesOption, setDeleteSeriesOption] = useState<"SINGLE" | "SERIES">("SINGLE");
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  const {
    data: holidaysResponse,
    isLoading: isFetchingHolidays,
    isFetching,
  } = useGetHolidaysQuery({ year: selectedYear });

  const [createHoliday, { isLoading: isCreating }] = useCreateHolidayMutation();
  const [updateHoliday, { isLoading: isUpdating }] = useUpdateHolidayMutation();
  const [deleteHoliday, { isLoading: isDeleting }] = useDeleteHolidayMutation();

  const allHolidays = holidaysResponse?.data || [];

  const statCards = useMemo(() => {
    const total = allHolidays.length;
    const national = allHolidays.filter((h: IHoliday) => h.isMandatory).length;
    const optional = total - national;
    return [
      { icon: "calendar-days", label: `Total Holidays ${selectedYear}`, value: total, iconBg: "var(--color-primary-100)", iconColor: "var(--color-primary-600)" },
      { icon: "flag", label: "National / Mandatory", value: national, iconBg: "var(--color-error-light)", iconColor: "var(--color-error-dark)" },
      { icon: "map-pin", label: "Regional / Optional", value: optional, iconBg: "var(--color-warning-light)", iconColor: "var(--color-warning-dark)" },
    ];
  }, [allHolidays, selectedYear]);

  const formatDateParts = (dateString: string) => {
    const d = new Date(dateString);
    return {
      day: d.getDate(),
      month: MONTHS[d.getMonth()],
      weekday: WEEKDAYS[d.getDay()],
      full: d.toLocaleDateString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
      }),
    };
  };

  const [formData, setFormData] = useState<IHolidayFormData>({
    name: "",
    date: "",
    year: "",
    isMandatory: true,
    description: "",
    isRecurring: false,
  });

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error("Holiday name is required");
      return false;
    }
    if (!formData.date) {
      toast.error("Please select a date");
      return false;
    }
    const selectedDate = dayjs(formData.date);
    if (!selectedDate.isValid()) {
      toast.error("Invalid date selected");
      return false;
    }
    if (!selectedYear) {
      toast.error("Please select a year");
      return false;
    }
    if (selectedDate.year() !== Number(formData.year)) {
      toast.error("Please select a valid year according to selected date");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canManage || !validateForm()) return;

    const yearValue = Number(formData.year);
    if (Number.isNaN(yearValue) || yearValue <= 0) {
      toast.error("Please select a valid year");
      return;
    }

    const submitData = {
      ...formData,
      year: yearValue,
      isRecurring: formData.isRecurring,
    };

    try {
      await (editingHoliday?.id
        ? updateHoliday({ id: editingHoliday.id, ...submitData })
        : createHoliday(submitData)
      ).unwrap();

      toast.success(
        editingHoliday
          ? "Holiday updated successfully!"
          : "Holiday created successfully!"
      );
      setEditingHoliday(null);
      resetForm();
      setShowAddModal(false);
    } catch (err) {
      const error = err as IApiError;
      const msg = error?.data?.message || error?.message || "Operation failed";
      toast.error(msg);
      console.error(error);
    }
  };

  const handleDelete = (holiday: IHoliday) => {
    if (!canManage) return;
    setHolidayToDelete(holiday);
    setDeleteSeriesOption("SINGLE");
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteHoliday = async (): Promise<void> => {
    if (!canManage || !holidayToDelete?.id) return;
    try {
      const isSeries = Boolean(
        holidayToDelete.recurringGroupId && deleteSeriesOption === "SERIES"
      );
      await deleteHoliday({
        id: holidayToDelete.id,
        deleteSeries: isSeries,
      }).unwrap();
      toast.success(
        isSeries
          ? "Recurring holiday series deleted successfully!"
          : "Holiday deleted successfully!"
      );
      setShowDeleteConfirmation(false);
      setHolidayToDelete(null);
    } catch (err) {
      const error = err as IApiError;
      const msg = error?.data?.message || error?.message || "Delete failed";
      toast.error(msg);
      console.error(error);
    }
  };

  const openEditModal = (holiday: IHoliday) => {
    if (!canManage) return;
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date.split("T")[0],
      year: String(holiday.year),
      isMandatory: holiday.isMandatory,
      description: holiday.description || "",
      isRecurring: Boolean(holiday.isRecurring),
    });
    setShowAddModal(true);
  };

  const openAddModal = () => {
    setEditingHoliday(null);
    resetForm();
    if (selectedYear) {
      setFormData((prev) => ({ ...prev, year: String(selectedYear) }));
    }
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      date: "",
      year: "",
      isMandatory: true,
      description: "",
      isRecurring: false,
    });
  };

  const years = useMemo(() => getHolidayYears(currentYear), [currentYear]);

  return (
    <div className="w-full">
      <PageHeader
        title="Holiday Management"
        description="Configure the company holiday calendar."
        breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "Holiday Management" }]}
        actions={
          <div className="flex items-center gap-2 sm:gap-3">
              {canManage && (
                <Button
                  appearance="secondary"
                  size="large"
                  icon={<Upload className="w-4 h-4" />}
                  onClick={() => setShowBulkUploadModal(true)}
                >
                  Bulk Upload
                </Button>
              )}
              {canManage && (
                <Button
                  appearance="primary"
                  size="large"
                  icon={<Plus className="w-4 h-4" />}
                  onClick={openAddModal}
                >
                  Add Holiday
                </Button>
              )}
            </div>
          }
        />

        {isFetchingHolidays ? (
          <HolidaysSectionSkeleton hasActions={canManage} />
        ) : (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-5 sm:mb-6">
              {statCards.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white border border-[var(--color-border-secondary)] rounded-[18px] p-[18px] sm:p-6 flex items-center gap-3 sm:gap-4 transition-all duration-150 hover:-translate-y-[3px] hover:shadow-[var(--shadow-md)]"
                >
                  <span
                    className="w-11 h-11 rounded-[12px] flex-none grid place-items-center"
                    style={{ background: stat.iconBg }}
                  >
                    {stat.icon === "calendar-days" && <Calendar className="w-5 h-5" style={{ color: stat.iconColor }} />}
                    {stat.icon === "flag" && <Calendar className="w-5 h-5" style={{ color: stat.iconColor }} />}
                    {stat.icon === "map-pin" && <Calendar className="w-5 h-5" style={{ color: stat.iconColor }} />}
                  </span>
                  <div>
                    <div className="text-[22px] font-extrabold leading-none text-[var(--color-text-primary)] tracking-tight">
                      {stat.value}
                    </div>
                    <div className="text-[12px] font-semibold text-[var(--color-text-tertiary)] mt-1">
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Year Filter */}
            <div className="flex items-center justify-between mb-4 sm:mb-5">
              <h3 className="text-base sm:text-lg font-bold text-[var(--color-text-primary)] m-0">
                Holiday List
              </h3>
              <div className="w-32 sm:w-40">
                <Select
                  className="w-full"
                  searchable
                  ariaLabel="Filter holidays by year"
                  value={String(selectedYear)}
                  onChange={(v) => setSelectedYear(Number(v))}
                  options={years.map((y) => ({ label: y, value: y }))}
                  size="sm"
                />
              </div>
            </div>

            {/* Holiday Cards Grid */}
            {allHolidays.length === 0 ? (
              <div className="bg-white border border-[var(--color-border-secondary)] rounded-[18px] p-10 text-center">
                <Calendar className="w-12 h-12 text-[var(--color-text-tertiary)] mx-auto mb-4" />
                <p className="text-[var(--color-text-secondary)] font-medium">
                  No holidays found for {selectedYear}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {allHolidays.map((holiday: IHoliday) => {
                  const parts = formatDateParts(holiday.date);
                  const typeColor = holiday.isMandatory ? "var(--color-error-dark)" : "var(--color-warning-dark)";
                  const typeBg = holiday.isMandatory ? "var(--color-error-light)" : "var(--color-warning-light)";
                  const isMutating = isCreating || isUpdating || isDeleting;

                  return (
                    <div
                      key={holiday.id}
                      className="bg-white border border-[var(--color-border-secondary)] rounded-[18px] p-[18px] sm:p-5 flex items-center gap-3 sm:gap-4 transition-all duration-150 hover:-translate-y-[3px] hover:shadow-[var(--shadow-md)]"
                    >
                      {/* Date Block */}
                      <div
                        className="w-[56px] flex-none text-center rounded-[13px] py-[9px]"
                        style={{ background: typeBg }}
                      >
                        <div className="text-[21px] font-extrabold leading-none tracking-tight" style={{ color: typeColor }}>
                          {parts.day}
                        </div>
                        <div className="text-[10.5px] font-bold uppercase mt-[2px]" style={{ color: typeColor, opacity: 0.85 }}>
                          {parts.month}
                        </div>
                      </div>

                      {/* Holiday Info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-[14.5px] font-bold text-[var(--color-text-primary)] truncate">
                          {holiday.name}
                        </div>
                        <div className="text-[12px] text-[var(--color-text-tertiary)] mt-[2px]">
                          {parts.weekday}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          <span
                            className="inline-flex text-[10.5px] font-bold px-[9px] py-[3px] rounded-full"
                            style={{ color: typeColor, background: typeBg }}
                          >
                            {holiday.isMandatory ? "National" : "Optional"}
                          </span>
                          {holiday.isRecurring && (
                            <span
                              className="inline-flex items-center gap-1 text-[10.5px] font-bold px-[9px] py-[3px] rounded-full text-[var(--color-primary-700)] bg-[var(--color-primary-100)] border border-[var(--color-primary-200)]"
                            >
                              <Repeat className="w-2.5 h-2.5" />
                              Repeats Yearly
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {canManage && (
                        <div className="flex flex-col gap-1 flex-none">
                          <button
                            onClick={() => openEditModal(holiday)}
                            disabled={isMutating}
                            className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-primary-100)] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <SquarePen className="w-[15px] h-[15px] text-[var(--color-text-tertiary)]" />
                          </button>
                          <button
                            onClick={() => handleDelete(holiday)}
                            disabled={isMutating}
                            className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-error-light)] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isDeleting && holidayToDelete?.id === holiday.id ? (
                              <Loader2 className="w-[15px] h-[15px] text-[var(--color-error-dark)] animate-spin" />
                            ) : (
                              <Trash2 className="w-[15px] h-[15px] text-[var(--color-text-tertiary)]" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

      {/* Add / Edit Holiday Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingHoliday(null);
        }}
        title={
          <div>
            <div className="text-lg font-extrabold text-[var(--color-text-primary)] tracking-tight">
              {editingHoliday ? "Edit Holiday" : "Add Holiday"}
            </div>
            <div className="text-[12.5px] text-[var(--color-text-tertiary)] mt-[4px] font-medium">
              {editingHoliday
                ? "Update the holiday details"
                : "Add a date to the company calendar"}
            </div>
          </div>
        }
        bodyClassName="!p-0"
      >
        <form onSubmit={handleSubmit}>
          <div className="p-5 sm:p-6 flex flex-col gap-4 sm:gap-5">
            <Input
              label="Holiday Name"
              required
              placeholder="e.g., New Year's Day"
              value={formData.name}
              onChange={(v) => setFormData({ ...formData, name: v as string })}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => {
                    const d = e.target.value;
                    setFormData({ ...formData, date: d, year: d ? d.split("-")[0] : formData.year });
                  }}
                  className="w-full h-11 px-3.5 bg-[var(--color-bg-muted)] border border-[var(--color-border-primary)] rounded-[11px] text-[13.5px] text-[var(--color-text-primary)] outline-none transition-all duration-150 focus:bg-white focus:border-[var(--color-primary-600)]/40 focus:shadow-[0_0_0_3px_rgba(124,58,237,0.14)] font-inherit"
                />
              </div>
              <Select
                className="w-full"
                placeholder="Select Year"
                label="Year"
                required
                value={formData.year}
                onChange={(v) => setFormData({ ...formData, year: v as string })}
                options={years.map((y) => ({ label: y, value: y }))}
              />
            </div>
            <div>
              <label className="block text-[12.5px] font-semibold text-[var(--color-text-primary)] mb-[7px]">
                Type
              </label>
              <RadioButton
                name="mandatory"
                value={formData.isMandatory ? "mandatory" : "optional"}
                onChange={(v) =>
                  setFormData({ ...formData, isMandatory: v === "mandatory" })
                }
                options={[
                  { label: "National", value: "mandatory" },
                  { label: "Optional", value: "optional" },
                ]}
                direction="row"
              />
            </div>
            {/* Recur Holiday Toggle */}
            <div className="flex items-center justify-between p-3.5 rounded-[12px] bg-[var(--color-bg-muted)] border border-[var(--color-border-primary)] transition-colors hover:border-[var(--color-border-secondary)]">
              <div>
                <label
                  htmlFor="recur-holiday-toggle"
                  className="block text-[13px] font-semibold text-[var(--color-text-primary)] cursor-pointer"
                >
                  Recur Holiday
                </label>
                <span className="block text-[11.5px] text-[var(--color-text-tertiary)] mt-0.5">
                  Repeat this holiday for upcoming years
                </span>
              </div>
              <button
                id="recur-holiday-toggle"
                type="button"
                role="switch"
                aria-checked={formData.isRecurring}
                aria-label="Recur Holiday"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    isRecurring: !prev.isRecurring,
                  }))
                }
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-600)] focus:ring-offset-2 ${
                  formData.isRecurring
                    ? "bg-[var(--color-primary-600)]"
                    : "bg-gray-300 dark:bg-gray-600"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                    formData.isRecurring ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <TextArea
              label="Description (optional)"
              value={formData.description}
              onChange={(v) =>
                setFormData({ ...formData, description: v })
              }
              placeholder="Add notes about this holiday..."
            />
          </div>
          <ModalFooter
            justify="end"
            className="!px-5 sm:!px-6 !py-[18px] border-t border-[var(--color-layout-dropdown-separator)]"
          >
            <Button
              htmlType="button"
              appearance="secondary"
              onClick={() => {
                setShowAddModal(false);
                setEditingHoliday(null);
              }}
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              appearance="primary"
              loading={isCreating || isUpdating}
              disabled={isCreating || isUpdating}
            >
              {editingHoliday ? "Update Holiday" : "Add Holiday"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <ConfirmationModal
        type="danger"
        title="Delete Holiday"
        message={
          holidayToDelete?.recurringGroupId ? (
            <div className="space-y-3">
              <p className="text-sm text-[var(--color-text-secondary)]">
                This is a recurring holiday. How would you like to delete it?
              </p>
              <div className="flex flex-col gap-2 pt-1 text-left">
                <label className="flex items-center gap-2.5 p-2 rounded-lg border border-[var(--color-border-primary)] hover:bg-[var(--color-bg-muted)] cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="deleteSeriesOption"
                    value="SINGLE"
                    checked={deleteSeriesOption === "SINGLE"}
                    onChange={() => setDeleteSeriesOption("SINGLE")}
                    className="accent-[var(--color-primary-600)]"
                  />
                  <span className="text-[13px] font-medium text-[var(--color-text-primary)]">
                    Delete this year only
                  </span>
                </label>
                <label className="flex items-center gap-2.5 p-2 rounded-lg border border-[var(--color-border-primary)] hover:bg-[var(--color-bg-muted)] cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="deleteSeriesOption"
                    value="SERIES"
                    checked={deleteSeriesOption === "SERIES"}
                    onChange={() => setDeleteSeriesOption("SERIES")}
                    className="accent-[var(--color-primary-600)]"
                  />
                  <span className="text-[13px] font-medium text-[var(--color-text-primary)]">
                    Delete this and all upcoming years in this series
                  </span>
                </label>
              </div>
            </div>
          ) : (
            "Are you sure you want to delete this holiday? This action cannot be undone."
          )
        }
        onConfirm={confirmDeleteHoliday}
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setHolidayToDelete(null);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />

      {canManage && (
        <HolidayBulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            setShowBulkUploadModal(false);
            toast.success("Holidays uploaded successfully!");
          }}
        />
      )}
    </div>
  );
};

export default HolidayManagement;
